import React, { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Modal from '../ui/Modal';
import { RefreshCw, Search, ChevronLeft, ChevronRight, Mail, Download } from 'lucide-react';

// Field groups for the export modal. Order within each group is preserved
// in the resulting XLSX. Backend whitelists every key — anything we add here
// that the backend doesn't recognize is silently dropped.
const EXPORT_FIELD_GROUPS = [
  {
    title: 'Basic info',
    fields: [
      { key: 'email', label: 'Email' },
      { key: 'full_name', label: 'Full name' },
      { key: 'company_name', label: 'Company name' },
      { key: 'phone_number', label: 'Phone number' },
    ],
  },
  {
    title: 'Account status',
    fields: [
      { key: 'is_active', label: 'Active' },
      { key: 'is_verified', label: 'Email verified' },
      { key: 'role', label: 'Role' },
      { key: 'admin_role', label: 'Admin role' },
    ],
  },
  {
    title: 'Approval',
    fields: [
      { key: 'approval_status', label: 'Approval status' },
      { key: 'approved_by', label: 'Approved by' },
      { key: 'approved_at', label: 'Approved at' },
    ],
  },
  {
    title: 'Activity',
    fields: [
      { key: 'created_at', label: 'Registered at' },
      { key: 'last_login_at', label: 'Last login' },
      { key: 'job_count', label: 'Job count' },
      { key: 'contacts_extracted', label: 'Contacts extracted' },
    ],
  },
  {
    title: 'Deactivation',
    fields: [
      { key: 'deactivated_by', label: 'Deactivated by' },
      { key: 'deactivated_at', label: 'Deactivated at' },
      { key: 'deactivation_reason', label: 'Deactivation reason' },
    ],
  },
];

// Sensible defaults so admins don't have to tick everything every time.
const DEFAULT_EXPORT_FIELDS = [
  'email', 'full_name', 'company_name', 'phone_number',
  'created_at', 'is_active', 'is_verified', 'role',
  'job_count', 'contacts_extracted',
];

const PagedUsersTable = ({ appSettings, renderActions, refreshSignal }) => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Export modal state
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFields, setExportFields] = useState(DEFAULT_EXPORT_FIELDS);
  const [exporting, setExporting] = useState(false);

  const toggleExportField = (key) => {
    setExportFields(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const setGroupSelection = (groupKeys, selected) => {
    setExportFields(prev => {
      if (selected) {
        // Union without duplicates while preserving existing order.
        const merged = [...prev];
        groupKeys.forEach(k => { if (!merged.includes(k)) merged.push(k); });
        return merged;
      }
      return prev.filter(k => !groupKeys.includes(k));
    });
  };

  const handleExport = async () => {
    if (exportFields.length === 0) {
      alert('Please select at least one field to export.');
      return;
    }
    setExporting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`${API_BASE_URL}/api/admin/users/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fields: exportFields, q: search || undefined }),
      });
      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        throw new Error(`Export failed (${resp.status}): ${errText}`);
      }
      // Trigger browser download from the streamed XLSX blob.
      const blob = await resp.blob();
      const filename = (() => {
        const cd = resp.headers.get('Content-Disposition') || '';
        const match = cd.match(/filename="?([^"]+)"?/);
        return match ? match[1] : `users_export.xlsx`;
      })();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (e) {
      console.error(e);
      alert(`Failed to export users: ${e.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  const fetchPage = async (p = page, sBy = sortBy, sDir = sortDir, size = pageSize, query = search) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = new URL(`${API_BASE_URL}/api/admin/users/paged`);
      url.searchParams.set('page', String(p));
      url.searchParams.set('page_size', String(size));
      url.searchParams.set('sort_by', sBy);
      url.searchParams.set('sort_dir', sDir);
      if (query && query.trim()) url.searchParams.set('q', query.trim());
      const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Failed to fetch users');
      const data = await resp.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPageSize(data.page_size || 50);
      setSortBy(data.sort_by || sBy);
      setSortDir(data.sort_dir || sDir);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPage(1, 'created_at', 'desc', 50, ''); }, []);
  useEffect(() => { if (refreshSignal) fetchPage(page, sortBy, sortDir, pageSize, search); }, [refreshSignal]);

  const pages = Math.ceil(total / pageSize) || 1;

  const headerCell = (label, key) => (
    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}
        onClick={() => {
          const nextDir = sortBy === key && sortDir === 'desc' ? 'asc' : 'desc';
          setSortBy(key); setSortDir(nextDir); fetchPage(page, key, nextDir);
        }}>
      {label} {sortBy === key ? (sortDir === 'desc' ? '▼' : '▲') : ''}
    </th>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Ensure backend timestamps are parsed as UTC by adding 'Z' when missing
      let utcDateString = dateString;
      if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 19)) {
        utcDateString = dateString + 'Z';
      }
      const date = new Date(utcDateString);
      const timezone = appSettings?.timezone || 'UTC';
      return date.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <Card>
      <Card.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Card.Title>Users</Card.Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <input
                placeholder="Search email, name, company, phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchPage(1, sortBy, sortDir, pageSize, e.target.value); }}
                style={{ padding: '8px 28px 8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, width: 320 }}
              />
              <Search size={14} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            </div>
            <Button variant="secondary" onClick={() => fetchPage(page, sortBy, sortDir, pageSize, search)} icon={<RefreshCw size={14} />}>Refresh</Button>
            <Button variant="secondary" onClick={() => setExportOpen(true)} icon={<Download size={14} />}>Export</Button>
          </div>
        </div>
      </Card.Header>
      <Card.Content>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                {headerCell('User', 'full_name')}
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Contact</th>
                {headerCell('Company', 'company_name')}
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Jobs</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Contacts</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Status</th>

                {headerCell('Registered', 'created_at')}
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="8" style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No users found</td></tr>
              ) : (
                items.map(user => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px', height: '40px', borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                          fontWeight: 600, fontSize: 16
                        }}>
                          {user.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p style={{ fontWeight: 600, color: '#111827', margin: 0, fontSize: 14 }}>{user.full_name || 'N/A'}</p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>
                      {user.phone_number || 'N/A'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>
                      {user.company_name || 'N/A'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>
                      {user.job_count || 0}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>
                      {user.contacts_extracted ? user.contacts_extracted.toLocaleString() : 0}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, backgroundColor: user.is_active ? '#d1fae5' : '#fee2e2', color: user.is_active ? '#10b981' : '#ef4444', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 8 }}>
                          {user.is_active ? '● Active' : '● Inactive'}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, backgroundColor: '#eef2ff', color: '#4f46e5', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 8 }}>
                          {(user.role === 'admin' ? `Admin (${user.admin_role || 'admin'})` : 'User')}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, backgroundColor: user.is_verified ? '#d1fae5' : '#fee2e2', color: user.is_verified ? '#10b981' : '#ef4444', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 8 }}>
                          {user.is_verified ? 'Email Verified' : 'Email Unverified'}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, backgroundColor: (user.approval_status === 'approved' ? '#d1fae5' : user.approval_status === 'pending' ? '#fef3c7' : '#fee2e2'), color: (user.approval_status === 'approved' ? '#10b981' : user.approval_status === 'pending' ? '#92400e' : '#ef4444'), fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 8 }}>
                          {user.approval_status ? `Approval: ${user.approval_status}` : 'Approval: n/a'}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>{formatDate(user.created_at)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {renderActions ? renderActions(user) : (
                        <Button size="sm" variant="secondary" icon={<Mail size={14} />} onClick={(e) => { e.stopPropagation(); }}>
                          Email
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Page {page} of {pages} • {total} total
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={() => { if (page > 1) { const np = page - 1; setPage(np); fetchPage(np, sortBy, sortDir, pageSize, search); } }} disabled={page === 1} icon={<ChevronLeft size={14} />}>Prev</Button>
            <Button variant="secondary" onClick={() => { if (page < pages) { const np = page + 1; setPage(np); fetchPage(np, sortBy, sortDir, pageSize, search); } }} disabled={page >= pages} icon={<ChevronRight size={14} />}>Next</Button>
          </div>
        </div>
      </Card.Content>

      <Modal
        isOpen={exportOpen}
        onClose={() => !exporting && setExportOpen(false)}
        title="Export users to XLSX"
        maxWidth="max-w-2xl"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            Pick the fields to include in the export. The current search filter
            <strong style={{ color: '#111827' }}>{search ? ` "${search}"` : ' (none)'}</strong> will be applied.
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setExportFields(EXPORT_FIELD_GROUPS.flatMap(g => g.fields.map(f => f.key)))}
            >Select all</Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setExportFields([])}
            >Deselect all</Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setExportFields(DEFAULT_EXPORT_FIELDS)}
            >Reset to defaults</Button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            {EXPORT_FIELD_GROUPS.map(group => {
              const groupKeys = group.fields.map(f => f.key);
              const allSelected = groupKeys.every(k => exportFields.includes(k));
              const someSelected = !allSelected && groupKeys.some(k => exportFields.includes(k));
              return (
                <div key={group.title} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{group.title}</div>
                    <button
                      type="button"
                      onClick={() => setGroupSelection(groupKeys, !allSelected)}
                      style={{ fontSize: 11, color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      {allSelected ? 'Clear' : (someSelected ? 'Select all' : 'Select all')}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {group.fields.map(field => (
                      <label key={field.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={exportFields.includes(field.key)}
                          onChange={() => toggleExportField(field.key)}
                        />
                        {field.label}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <Button variant="secondary" onClick={() => setExportOpen(false)} disabled={exporting}>Cancel</Button>
            <Button
              onClick={handleExport}
              disabled={exporting || exportFields.length === 0}
              icon={<Download size={14} />}
            >
              {exporting ? 'Generating...' : `Download XLSX (${exportFields.length} fields)`}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default PagedUsersTable;
